import { Heart, MoreHorizontal, Play, Pause, ExternalLink, Pencil, Trash2, Plus, Check, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePlayer, stationKey } from "@/context/PlayerContext";
import { StationLogo, Equalizer } from "@/components/radio/StationLogo";

const meta = (s) => [s.country, s.codec && `${s.codec}${s.bitrate ? ` ${s.bitrate}k` : ""}`].filter(Boolean).join(" · ");

export const PlayStationButton = ({ station, testid }) => {
  const p = usePlayer();
  const on = p.liveKey === stationKey(station);
  return (
    <button onClick={() => (on ? p.toggle() : p.playStation(station))} data-testid={testid} title={on && p.playing ? "Pause" : "Play"}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105 ${on ? "bg-amber-600 text-white" : "bg-zinc-900 text-white"}`}>
      {on && p.playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
    </button>
  );
};

export const StationCard = ({ s, onFavorite, onRename, onDelete }) => {
  const p = usePlayer();
  const on = p.liveKey === stationKey(s);
  return (
    <div data-testid={`station-card-${s.id}`}
      className={`group flex items-center gap-4 rounded-lg border bg-white p-4 transition-colors ${on ? "border-amber-400 shadow-[0_0_0_3px_rgba(245,158,11,0.12)]" : "border-zinc-200 hover:border-zinc-300"}`}>
      <StationLogo src={s.favicon} className="h-14 w-14" />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-sm font-medium">{s.name}{on && p.playing && <Equalizer />}</p>
        <p className="truncate text-xs text-zinc-500">{on && p.nowPlaying ? <span className="text-amber-700" data-testid={`station-now-${s.id}`}>{p.nowPlaying}</span> : meta(s) || (s.source === "custom" ? "Custom stream" : "")}</p>
        {s.tags?.length > 0 && <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-zinc-400">{s.tags.slice(0, 3).join(" · ")}</p>}
      </div>
      <button onClick={() => onFavorite(s)} data-testid={`station-favorite-${s.id}`} title="Favourite"
        className={`rounded-full p-2 transition-colors ${s.favorite ? "text-amber-600" : "text-zinc-300 hover:text-zinc-700"}`}>
        <Heart size={16} fill={s.favorite ? "currentColor" : "none"} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`station-menu-${s.id}`} className="rounded p-1.5 text-zinc-400 hover:text-zinc-900"><MoreHorizontal size={16} /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {s.homepage && <DropdownMenuItem asChild><a href={s.homepage} target="_blank" rel="noopener noreferrer" data-testid={`station-homepage-${s.id}`}><ExternalLink size={14} className="mr-2" />Website & schedule</a></DropdownMenuItem>}
          <DropdownMenuItem onClick={() => onRename(s)} data-testid={`station-rename-${s.id}`}><Pencil size={14} className="mr-2" />Rename</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDelete(s)} className="text-red-600" data-testid={`station-delete-${s.id}`}><Trash2 size={14} className="mr-2" />Remove station</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PlayStationButton station={s} testid={`station-play-${s.id}`} />
    </div>
  );
};

export const DirectoryRow = ({ s, onSave, saving }) => (
  <div className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-zinc-50/70" data-testid={`directory-row-${s.rb_uuid}`}>
    <StationLogo src={s.favicon} className="h-11 w-11" iconSize={16} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{s.name}</p>
      <p className="truncate text-xs text-zinc-500">{meta(s)}{s.tags.length ? <span className="text-zinc-400"> · {s.tags.slice(0, 3).join(", ")}</span> : null}</p>
    </div>
    <button onClick={() => onSave(s)} disabled={s.saved || saving} data-testid={`directory-save-${s.rb_uuid}`}
      className={`flex h-9 items-center gap-1.5 rounded-full px-4 text-xs transition-colors ${s.saved ? "bg-emerald-50 text-emerald-700" : "border border-zinc-300 hover:border-zinc-900"}`}>
      {saving ? <Loader2 size={13} className="animate-spin" /> : s.saved ? <Check size={13} /> : <Plus size={13} />}{s.saved ? "Saved" : "Save"}
    </button>
    <PlayStationButton station={s} testid={`directory-play-${s.rb_uuid}`} />
  </div>
);
